import React, { PureComponent } from "react";
import { EXPAND_COLUMN_WIDTH, DRAG_DELTA_DEADZONE, DEFAULT_SIDEBAR_WIDTH } from "../../helpers/Const";
import memoizeOne from "memoize-one";

import filterIcon from "../../../icons/ButtonDatePicker.png";
import filterIconHover from "../../../icons/ButtonDatePickerhover.png";
import filterIconPressed from "../../../icons/ButtonDatePickerPressed.png";
import { IHierarchyItem } from "src/backend/interfaces/IItem";
import { IObjectTypeConfigurationItem } from "src/backend/interfaces/configuration/IConfiguration";
import { addQueryParameter, parseColumnWidthsFromQueryString, parseExpandAllFromQueryString, parseSortByFromQueryString, removeQueryParameter } from "src/backend/helpers/urlHelper";
import { QUERYSTRING_COLUMN_WIDTHS, QUERYSTRING_EXPAND_ALL, QUERYSTRING_SORT_BY } from "src/backend/constants/common";

const imgs = [filterIcon, filterIconHover, filterIconPressed];

export interface ITimeLineCommonProps {
  scrollLeft: number;
  scrollLeftOnDragStart: number;
  dragTaskId: number;
  dragType: string;
  rootData: IHierarchyItem[];
  taskHeight?: number;
  sideBarWidth: number;
  visibleColumns?: string[];
  allData: IHierarchyItem[];
  onStartDrag: (e: React.MouseEvent<HTMLElement>, taskId: number, dragType: string) => void;
  onEndDrag: (taskId: number, viewIndex: number, dragType: string, extraData: number, isCancelled: boolean) => void;
  viewIndex: number;
  onHorizontalScroll: (newScrollLeft: number) => void;
  dragUnderwayPreliminary: boolean;
  mouseXDelta: number;
  mouseYDelta: number;
  loading: boolean;
  viewType: string;
  isLast: boolean;
  timelineTotalHeight: number;
  onTaskListSizing: (delta: number) => void;
  //onApplyTaskOnProperty?: any;
  startDate: Date;
  endDate: Date;
  hourWidth: number;
  zoomMode: string;
  chartTotalWidth: number;
  onHorizontalResize: (totalWidth: number) => void;
  onTimelineVerticalResizerDrag: (delta: number, index: number) => void;
  headerElem: JSX.Element;
  getObjectTypeConfig?: (objectTypeId: number) => IObjectTypeConfigurationItem,
}

interface IVisibleTaskColumn {
  name: string;
  width: number;
}

interface ISortOrder {
  columnName: string;
  isAscending: boolean;
}

interface ITimeLineCommonState {
  scrollTop: number;
  sideListInnerCalculatedHeight: number;
  viewPortInnerCalculatedHeight: number;
  expandedTasks: Set<number>;
  visibleTaskColumns: IVisibleTaskColumn[];
  sortOrder: ISortOrder[];
}

class TimeLineCommon extends PureComponent<ITimeLineCommonProps,ITimeLineCommonState> {

  viewPort: React.RefObject<HTMLDivElement>;
  viewPortRoot: React.RefObject<HTMLDivElement>;
  resizeRef: string;
  scrollTopOnDragStart: number;
  dragDeadZoneReached: boolean;

  constructor(props: ITimeLineCommonProps) {
    super(props);

    this.viewPort = React.createRef();
    this.viewPortRoot = React.createRef();

    const visibleData = this.getVisibleDataListSorted(this.props.rootData, new Set(), []);
    this.scrollTopOnDragStart = 0;
    this.dragDeadZoneReached = false;

    const rnd = Math.floor((Math.random() * 1000000) + 1);
    this.resizeRef = `tlrf${rnd}`;

    this.state = {
      scrollTop: 0,
      sideListInnerCalculatedHeight: visibleData.length * this.props.taskHeight, // Re-calculated later
      viewPortInnerCalculatedHeight: visibleData.length * this.props.taskHeight, // Re-calculated later
      expandedTasks: new Set(),
      visibleTaskColumns: this.getVisibleTaskColumns(),
      sortOrder: [],
    };
  }

  getVisibleTaskColumns = (): IVisibleTaskColumn[] => {
    const initialColumnWidth = (DEFAULT_SIDEBAR_WIDTH - EXPAND_COLUMN_WIDTH) / Math.min(this.props.visibleColumns.length,3);
    const urlColumnWidthsStr = parseColumnWidthsFromQueryString(this.props.viewIndex);
    const urlColumnWidths = urlColumnWidthsStr?.split("-").map(Number) ?? [];
    return this.props.visibleColumns.map<IVisibleTaskColumn>((columnName, columnIndex) => {
      let columnWidth = urlColumnWidths[columnIndex] ?? initialColumnWidth;
      if(!columnWidth || columnWidth < 10) { 
        columnWidth = 10;
      }
      return { name: columnName, width: Math.round(columnWidth) };
    });
  };

  updateSortToUrl = (sortOrder: ISortOrder): void => {
    if(sortOrder == null || (sortOrder.columnName === "id" && sortOrder.isAscending)) {
      const url = removeQueryParameter(window.location.href, QUERYSTRING_SORT_BY + this.props.viewIndex);
      window.history.replaceState({}, "", url);
    } else {
      const sortBy = sortOrder.columnName + (sortOrder.isAscending ? "_asc" : "_desc");
      const url = addQueryParameter(window.location.href, QUERYSTRING_SORT_BY + this.props.viewIndex, sortBy);
      window.history.replaceState({}, "", url);
    }
  }

  addSortColumn = (columnName: string): void => {
    const sortIndex = this.state.sortOrder.findIndex((element) => {
      return element.columnName === columnName;
    });
    if (sortIndex === 0) {
      // Same column clicked twice in sequence. Toggle between ascending, descending and unset.
      this.setState((prevState) => {
        const stateChanges = { sortOrder: prevState.sortOrder.slice() };
        const isAscending = stateChanges.sortOrder[0].isAscending;
        if(isAscending) {
          // Toggle to descending
          const sort = { columnName: columnName, isAscending: !isAscending };
          stateChanges.sortOrder.splice(sortIndex, 1);
          stateChanges.sortOrder.unshift(sort);
          this.updateSortToUrl(sort);
        } else {
          // Remove column from sort order
          stateChanges.sortOrder.splice(sortIndex, 1);
          const sort = stateChanges.sortOrder.shift();
          stateChanges.sortOrder.unshift(sort);
          this.updateSortToUrl(sort);
        }
        
        return stateChanges;
      });
    } else if (sortIndex > 0) {
      // Column somewhere in the sort order already. Move to the front.
      this.setState((prevState) => {
        const stateChanges = { sortOrder: prevState.sortOrder.slice() };
        const sort = { columnName: columnName, isAscending: true };
        stateChanges.sortOrder.splice(sortIndex, 1);
        stateChanges.sortOrder.unshift(sort);
        this.updateSortToUrl(sort);
        return stateChanges;
      });
    } else {
      // Add column to sort order.
      this.setState((prevState) => {
        var sortOrder = [{ columnName: columnName, isAscending: true }, ...prevState.sortOrder];
        this.updateSortToUrl(sortOrder[0]);
        return { sortOrder: sortOrder };
      });
    }
  };

  getVisibleDataListSorted = memoizeOne((rootData, expandedTasks, _columnSortOrder): IHierarchyItem[] => {
    // The columnSortOrder parameter is necessary for the memoize library to know when this function needs re-running.
    return this.findVisibleDataItemsColumnSorted([], rootData, expandedTasks);
  });

  findVisibleDataItemsColumnSorted = (result: IHierarchyItem[], data: IHierarchyItem[], expandedTasks: Set<number>): IHierarchyItem[] => {
    const dataSorted = data.sort(this.sortFuncColumn).filter((item) => item != null);
    for (const item of dataSorted) {
      result.push(item);
      if (expandedTasks.has(item.id)) {
        const children = item.children.map((childId) => this.props.allData[childId]);
        this.findVisibleDataItemsColumnSorted(result, children, expandedTasks);
      }
    }
    return result;
  };

  findAllDataItemsColumnSorted = (result: IHierarchyItem[], data: IHierarchyItem[]): IHierarchyItem[] => {
    const dataSorted = data.sort(this.sortFuncColumn).filter((item) => item != null);
    for (const item of dataSorted) {
      result.push(item);
      const children = item.children.map((childId) => this.props.allData[childId]);
      this.findAllDataItemsColumnSorted(result, children);
    }
    return result;
  };

  sortFuncColumn = (first: IHierarchyItem, second: IHierarchyItem): number => {
    for (const { columnName, isAscending } of this.state.sortOrder) {
      if (first[columnName] > second[columnName]) {
        return isAscending ? 1 : -1;
      } else if (first[columnName] < second[columnName]) {
        return isAscending ? -1 : 1;
      }
    }
    return 0;
  };

  toggleTaskExpand = (taskId: number): void => {
    this.setState((prevState) => {
      const stateChanges = { expandedTasks: new Set(prevState.expandedTasks) };
      if (stateChanges.expandedTasks.has(taskId)) {
        stateChanges.expandedTasks.delete(taskId);
      } else {
        stateChanges.expandedTasks.add(taskId);
      }
      return stateChanges;
    });
  };

  updateExpandToUrl = (allExpanded: boolean): void => {
    const url = allExpanded 
      ? addQueryParameter(window.location.href, QUERYSTRING_EXPAND_ALL + this.props.viewIndex, "1") 
      : removeQueryParameter(window.location.href, QUERYSTRING_EXPAND_ALL + this.props.viewIndex);
    window.history.replaceState({}, "", url);
  }

  toggleExpandAll = (): void => {
    let allExpanded = true;
    for (const { id } of Object.values(this.props.allData)) {
      if (!this.state.expandedTasks.has(id)) {
        allExpanded = false;
        break;
      }
    }

    if (allExpanded) {
      this.setState(() => {
        return { expandedTasks: new Set() };
      });
    } else {
      const taskIds = [];
      for (const { id } of Object.values(this.props.allData)) {
        taskIds.push(id);
      }
      this.setState(() => {
        return { expandedTasks: new Set(taskIds) };
      });
    }
    this.updateExpandToUrl(!allExpanded);
  };

  onStartDrag = (e: React.MouseEvent<HTMLElement>, taskId: number, dragType: string): void => {
    this.scrollTopOnDragStart = this.state.scrollTop;
    this.dragDeadZoneReached = false;
    this.props.onStartDrag(e, taskId, dragType);
  };

  onEndDrag = (taskId: number, dragType: string, extraData: number, isCancelled: boolean): void => {
    this.props.onEndDrag(taskId, this.props.viewIndex, dragType, extraData, isCancelled);
  };

  calculateStartEndRows = (listLength: number): {start: number, end: number} => {
    const new_start = Math.trunc(this.state.scrollTop / this.props.taskHeight);
    const numVisibleRows = Math.ceil(this.state.viewPortInnerCalculatedHeight / this.props.taskHeight) + 1;
    const new_end = Math.min(new_start + numVisibleRows, listLength);
    return { start: new_start, end: new_end };
  };

  updateScroll = (): void => {
    if (this.viewPortRoot.current.scrollLeft !== this.props.scrollLeft) {
      this.viewPortRoot.current.scrollLeft = this.props.scrollLeft;
    }
    if (this.viewPortRoot.current.scrollTop !== this.state.scrollTop) {
      this.viewPortRoot.current.scrollTop = this.state.scrollTop;
    }
  };

  componentDidMount(): void {
    for (const imgURL of imgs) {
      const img = new Image();
      img.src = imgURL;
    }
    this.updateScroll();
  }

  componentDidUpdate = (prevProps: ITimeLineCommonProps): void => {
    this.updateScroll();

    if (prevProps.sideBarWidth !== this.props.sideBarWidth) {
      // If the user resizes the side bar area and the total area is greater than the sum of all the columns,
      // stretch the last column to fill the gap.
      let columnTotalWidth = EXPAND_COLUMN_WIDTH;
      for (const columnData of this.state.visibleTaskColumns) {
        columnTotalWidth += columnData.width;
      }
      if (columnTotalWidth < this.props.sideBarWidth) {
        this.setState((prevState) => {
          const stateChanges = {
            visibleTaskColumns: [...prevState.visibleTaskColumns],
          };
          stateChanges.visibleTaskColumns[stateChanges.visibleTaskColumns.length - 1].width += this.props.sideBarWidth - columnTotalWidth;
          return stateChanges;
        });
      }
    }

    if (prevProps.visibleColumns.join(",") !== this.props.visibleColumns.join(",")) {
      this.setState({ visibleTaskColumns: this.getVisibleTaskColumns() });
    }

    if (prevProps.loading && !this.props.loading) {
      if(this.state.sortOrder.length === 0) {
        //data is loaded with id sorted by start and name, apply that sorting here as default sorting
        const defaultSort = [{ columnName: "id", isAscending: true }];
        var sortBy = parseSortByFromQueryString(this.props.viewIndex);
        if(sortBy) {
          const sortColumn = sortBy.split("_")[0];
          const isAscending = sortBy.split("_")[1] === "asc";
          const sortOrder = { columnName: sortColumn, isAscending: isAscending };
          defaultSort.unshift(sortOrder);
          this.updateSortToUrl(sortOrder);
        } else {
          this.updateSortToUrl(defaultSort[0]);
        }
        this.setState(() => {
          return { sortOrder: defaultSort };
        });
      }
      const expandAll = parseExpandAllFromQueryString(this.props.viewIndex);
      if (expandAll && parseInt(expandAll) > 0) {
        this.toggleExpandAll();
      }
    }
    
  };

  handleScroll = (): void => {
    this.handleViewPortVerticalScroll();
    this.handleHorizontalScroll();
  };

  handleViewPortVerticalScroll = (): void => {
    this.handleVerticalScroll(this.viewPortRoot.current.scrollTop);
  };

  handleVerticalScroll = (scrollTop: number): void => {
    if (scrollTop === this.state.scrollTop) {
      return;
    }
    this.setState({
      scrollTop: scrollTop,
    });
  };

  handleHorizontalScroll = (): void => {
    this.props.onHorizontalScroll(this.viewPortRoot.current.scrollLeft);
  };

  onResize = (width: number, height: number): void => {
    this.props.onHorizontalResize(width);
    this.viewPortVerticalSizeUpdate(height);
  };

  onListVerticalSizeUpdate = (_width: number, height: number): void => {
    this.setState({ sideListInnerCalculatedHeight: height });
  };

  viewPortVerticalSizeUpdate = (height: number): void => {
    this.setState({ viewPortInnerCalculatedHeight: height });
  };

  onColumnResize = (columnIndex: number, delta: number): void => {
    this.setState((prevState: ITimeLineCommonState) => {
      const stateChanges = {
        visibleTaskColumns: [...prevState.visibleTaskColumns],
      };
      stateChanges.visibleTaskColumns[columnIndex].width += delta;
      stateChanges.visibleTaskColumns[columnIndex].width = Math.max(stateChanges.visibleTaskColumns[columnIndex].width, 10);
      stateChanges.visibleTaskColumns[columnIndex].width = Math.min(stateChanges.visibleTaskColumns[columnIndex].width, 600);
      stateChanges.visibleTaskColumns[columnIndex].width = Math.round(stateChanges.visibleTaskColumns[columnIndex].width);
      const columnWidthArray = stateChanges.visibleTaskColumns.map(c => c.width).join("-");
      const url = addQueryParameter(window.location.href, QUERYSTRING_COLUMN_WIDTHS + this.props.viewIndex, columnWidthArray);
      window.history.replaceState({}, "", url);
      return stateChanges;
    });
  };

  getCommonRenderVars(): { visibleData: IHierarchyItem[], dragUnderway: boolean, rowInfo: {start: number, end:number}, taskListExtraHeight: number, xDelta: number, yDelta: number, dragTaskId: number, dragType: string } {

    const visibleData = this.getVisibleDataListSorted(this.props.rootData, this.state.expandedTasks, this.state.sortOrder);

    const rowInfo = this.calculateStartEndRows(visibleData.length);

    //The extra height for the task list represents the height of the
    //horizontal scroll bar present in the viewport.
    const taskListExtraHeight = this.state.sideListInnerCalculatedHeight - this.state.viewPortInnerCalculatedHeight;

    const scrollXDelta = this.props.scrollLeft - this.props.scrollLeftOnDragStart;
    const scrollYDelta = this.state.scrollTop - this.scrollTopOnDragStart;
    let dragTaskId = this.props.dragTaskId;
    let dragUnderway = this.props.dragUnderwayPreliminary;
    let dragType = this.props.dragType;
    let xDelta = dragUnderway ? scrollXDelta + this.props.mouseXDelta : 0;
    let yDelta = dragUnderway ? scrollYDelta + this.props.mouseYDelta : 0;
    // Dragging does not officially start until the deadzone has been reached. This means that tiny dragging motion
    // does not move/resize/etc. the tasks. This reduces accidental drags and makes clicking easier.
    if (!this.dragDeadZoneReached) {
      if (Math.abs(xDelta) > DRAG_DELTA_DEADZONE || Math.abs(yDelta) > DRAG_DELTA_DEADZONE) {
        this.dragDeadZoneReached = true;
      } else {
        xDelta = 0;
        yDelta = 0;
        dragType = null;
        dragUnderway = false;
        dragTaskId = null;
      }
    }
    return { visibleData, dragUnderway, rowInfo, taskListExtraHeight, xDelta, yDelta, dragTaskId, dragType };
  }
}

export default TimeLineCommon;
